/*!
 * consent-gate.js
 * Default-deny gate for non-essential third-party scripts, network requests and cookies.
 * Reads Consentmo's cookie `cookieconsent_preferences_disabled` (comma-separated list of denied
 * groups: functionality, analytics, marketing). No cookie = nothing accepted yet = everything denied.
 * Must be the first script in <head>.
 *
 * Public API:
 *   window.cgAllowed(category)            -> boolean
 *   window.cgLoad(category, src, attrs)   -> loads a script only if the category is allowed
 *
 * Granting consent starts the blocked scripts in place (no reload). Revoking it reloads once, keeping the
 * scroll position, so trackers that were already running stop.
 */
(function (w, d) {
  'use strict';
  if (w.__cg) return;

  var COOKIE_NAME = 'cookieconsent_preferences_disabled';
  var CATS = ['functionality', 'analytics', 'marketing'];

  /* ---- Rules. Anything that does not match is treated as necessary and never blocked. ---- */
  var URL_RULES = [
    ['analytics', /(^|\.)(posthog\.com|amplitude\.com|hotjar\.(com|io)|luckyorange\.(net|com)|d10lpsik1i8c69\.cloudfront\.net|logr-in\.com|lr-ingest\.io|logrocket\.(com|io)|getlimy\.ai|clarity\.ms|mixpanel\.com|heapanalytics\.com|fullstory\.com|google-analytics\.com|googletagmanager\.com|shoplift\.ai)(\/|:|$)|shgcdn3\.com\/pixel-collector/i],
    ['marketing', /(^|\.)(klaviyo\.com|attn\.tv|attentivemobile\.com|liadm\.com|facebook\.net|facebook\.com\/tr|connect\.facebook\.net|tiktok\.com|redditstatic\.com|reddit\.com\/rp\.gif|alb\.reddit\.com|pinterest\.com|pinimg\.com\/ct|rakuten\.com|rakutenadvertising\.io|shopmy\.us|upsellit\.com|aimerce\.ai|rebuyengine\.com|getgobot\.com|gorgias-convert\.com|9gtb\.com|doubleclick\.net|googlesyndication\.com|googleadservices\.com|bat\.bing\.com|snapchat\.com|sc-static\.net|criteo\.(com|net)|taboola\.com)(\/|:|$)|\/apps\/shop\/ilzzb\/|s3-us-west-2\.amazonaws\.com\/jsstore\//i],
    /* Client decision: only marketing and analytics are gated. Functional apps (chat, wishlist, loyalty, reviews,
       app banner, shipping protection, booking, search) run for every visitor. */
    ['functionality', /^(___cg_none___)$/]
  ];

  var COOKIE_RULES = [
    ['analytics', /^(_ga($|_)|_gid$|_gat|_gac_|_hj|ph_|amp_|AMP_|_lr_|__lo|_lo_|lo_|_clck|_clsk|ttcsid|_shg_|Shoplift_|_vwo|_dd_s)/],
    ['marketing', /^(_fbp|_fbc|fr$|_ttp|_tt_|rdt_|_rdt|_pin_|_pinterest|_derived_epik|__kla|_kla|__attentive|__attn|_attn|attntv|_li_|lidid|_lc2_|_gcl|_gads|IDE$|_uet|_scid|_sctr|_gb(sess|t|test)$|apt_pixel|_ruid|_rsession)/],
    ['functionality', /^(___cg_none___)/]
  ];

  /* ---- Consent state ---- */
  var cookieDesc = Object.getOwnPropertyDescriptor(Document.prototype, 'cookie') ||
                   Object.getOwnPropertyDescriptor(HTMLDocument.prototype, 'cookie');
  function rawGet() { return cookieDesc.get.call(d); }
  function rawSet(v) { cookieDesc.set.call(d, v); }

  function readCookie(name) {
    var m = rawGet().match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : null;
  }
  function readDenied() {
    var v = readCookie(COOKIE_NAME);
    if (v === null) return CATS.slice();
    return v.split(',').map(function (s) { return s.trim(); }).filter(Boolean);
  }

  var denied = readDenied();
  var hits = 0;

  function isDenied(cat) { return !!cat && denied.indexOf(cat) !== -1; }
  function categoryFor(rules, str) {
    for (var i = 0; i < rules.length; i++) if (rules[i][1].test(str)) return rules[i][0];
    return null;
  }
  function urlBlocked(url) {
    if (!url) return false;
    var s = String(url);
    if (s.indexOf('data:') === 0 || s.indexOf('blob:') === 0) return false;
    var cat = categoryFor(URL_RULES, s.replace(/^[a-z]+:\/\//i, '').replace(/^\/\//, ''));
    if (cat && isDenied(cat)) { hits++; return true; }
    return false;
  }
  function cookieBlocked(name) {
    var cat = categoryFor(COOKIE_RULES, name);
    if (cat && isDenied(cat)) { hits++; return true; }
    return false;
  }

  /* ---- document.cookie guard ---- */
  try {
    Object.defineProperty(d, 'cookie', {
      configurable: true,
      get: function () { return rawGet(); },
      set: function (v) {
        var name = String(v).split('=')[0].trim();
        if (cookieBlocked(name)) return;
        rawSet(v);
      }
    });
  } catch (e) {}

  /* ---- Element src guards (dynamic script / iframe / image creation) ---- */
  function patchSrc(proto, isScript) {
    var desc = Object.getOwnPropertyDescriptor(proto, 'src');
    if (!desc || !desc.set) return;
    Object.defineProperty(proto, 'src', {
      configurable: true,
      enumerable: desc.enumerable,
      get: desc.get,
      set: function (v) {
        if (urlBlocked(v)) {
          try { this.setAttribute('data-cg-blocked-src', String(v)); } catch (e) {}
          if (isScript) { try { this.type = 'javascript/blocked'; } catch (e) {} }
          return;
        }
        desc.set.call(this, v);
      }
    });
  }
  patchSrc(HTMLScriptElement.prototype, true);
  patchSrc(HTMLIFrameElement.prototype, false);
  patchSrc(HTMLImageElement.prototype, false);

  var origSetAttribute = Element.prototype.setAttribute;
  Element.prototype.setAttribute = function (name, value) {
    if (typeof name === 'string' && name.toLowerCase() === 'src') {
      var tag = this.tagName;
      if ((tag === 'SCRIPT' || tag === 'IFRAME' || tag === 'IMG') && urlBlocked(value)) {
        origSetAttribute.call(this, 'data-cg-blocked-src', String(value));
        if (tag === 'SCRIPT') origSetAttribute.call(this, 'type', 'javascript/blocked');
        return;
      }
    }
    return origSetAttribute.call(this, name, value);
  };

  /* ---- Network guards ---- */
  if (w.fetch) {
    var origFetch = w.fetch;
    w.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url);
      if (urlBlocked(url)) return Promise.reject(new TypeError('Blocked by consent gate'));
      return origFetch.apply(this, arguments);
    };
  }
  if (w.XMLHttpRequest) {
    var xhrOpen = XMLHttpRequest.prototype.open;
    var xhrSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      this.__cgBlocked = urlBlocked(url);
      return xhrOpen.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function () {
      if (this.__cgBlocked) { try { this.abort(); } catch (e) {} return; }
      return xhrSend.apply(this, arguments);
    };
  }
  if (navigator.sendBeacon) {
    var origBeacon = navigator.sendBeacon.bind(navigator);
    navigator.sendBeacon = function (url) {
      if (urlBlocked(url)) return false;
      return origBeacon.apply(navigator, arguments);
    };
  }

  /* ---- Parser-inserted elements (scripts / iframes / images written in HTML) ---- */
  function neutralise(n) {
    if (!n || n.nodeType !== 1) return;
    var tag = n.tagName;
    if (tag !== 'SCRIPT' && tag !== 'IFRAME' && tag !== 'IMG') return;
    var src = n.getAttribute('src');
    if (!src || !urlBlocked(src)) return;
    n.setAttribute('data-cg-blocked-src', src);
    n.removeAttribute('src');
    if (tag === 'SCRIPT') n.type = 'javascript/blocked';
  }
  try {
    new MutationObserver(function (list) {
      for (var i = 0; i < list.length; i++) {
        var added = list[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          neutralise(n);
          if (n.querySelectorAll) {
            var kids = n.querySelectorAll('script[src],iframe[src],img[src]');
            for (var k = 0; k < kids.length; k++) neutralise(kids[k]);
          }
        }
      }
    }).observe(d.documentElement, { childList: true, subtree: true });
  } catch (e) {}
  d.addEventListener('beforescriptexecute', function (e) {
    var s = e.target;
    if (s && s.src && urlBlocked(s.src)) e.preventDefault();
  }, true);


  /* ---- localStorage / sessionStorage guard (app embeds that store tracking data) ---- */
  var STORAGE_RULES = [
    ['analytics', /^(amp_|AMP_|amplitude_|ph_|__ph_|_lr_|__lr_|_shg_|shoplift_|Shoplift_)/],
    ['marketing', /^(__aim_|_aim_|aim-|__kla|_kla|klaviyo|kl-|attn_|_attn|__attentive|_li_|_pin_|_gcl_ls|TW_EXT_|_rsession$|_ruid$|__rm)/],
    ['functionality', /^(___cg_none___)$/]
  ];
  function storageBlocked(key) {
    var cat = categoryFor(STORAGE_RULES, String(key));
    if (cat && isDenied(cat)) { hits++; return true; }
    return false;
  }
  try {
    var rawSetItem = w.Storage.prototype.setItem;
    w.Storage.prototype.setItem = function (k, v) {
      if (storageBlocked(k)) return;
      return rawSetItem.apply(this, arguments);
    };
  } catch (e) {}
  function purgeStorage() {
    ['localStorage', 'sessionStorage'].forEach(function (t) {
      try {
        var st = w[t];
        Object.keys(st).forEach(function (k) { if (storageBlocked(k)) st.removeItem(k); });
      } catch (e) {}
    });
  }

  /* ---- Cleanup of already-set cookies for denied groups ---- */
  function expireCookie(name) {
    var host = location.hostname.split('.');
    var domains = [null];
    for (var i = 0; i < host.length - 1; i++) domains.push('.' + host.slice(i).join('.'));
    domains.forEach(function (dom) {
      rawSet(name + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/' + (dom ? '; domain=' + dom : ''));
    });
  }
  function purgeDenied() {
    rawGet().split('; ').forEach(function (c) {
      var name = c.split('=')[0];
      var cat = categoryFor(COOKIE_RULES, name);
      if (cat && isDenied(cat)) expireCookie(name);
    });
  }
  purgeDenied();
  purgeStorage();

  /* ---- React to consent changes ----
     Only categories that actually have rules count. A first visit goes from "no cookie" (everything denied) to
     Consentmo's default (e.g. "marketing,analytics"); that only drops 'functionality', which gates nothing, so
     it must not reload. Grants start the blocked scripts in place; only a revocation reloads (trackers are
     already running), and it puts the visitor back where they were. A reload mid-scroll threw mobile shoppers
     to the top of the page (Shopify's shop.app recognition bounce on the second load also lands at the top). */
  var GATED = ['analytics', 'marketing'];
  var SCROLL_KEY = 'cg_scroll';
  function gatedDenied(list) {
    return list.filter(function (c) { return GATED.indexOf(c) !== -1; }).sort();
  }

  function activateAllowed() {
    var els = d.querySelectorAll('[data-cg-blocked-src]');
    for (var i = 0; i < els.length; i++) {
      var el = els[i], src = el.getAttribute('data-cg-blocked-src');
      if (urlBlocked(src)) continue;
      el.removeAttribute('data-cg-blocked-src');
      if (el.tagName !== 'SCRIPT') { el.src = src; continue; }
      if (!el.parentNode) continue;
      var s = d.createElement('script');
      for (var a = 0; a < el.attributes.length; a++) {
        var at = el.attributes[a];
        if (at.name !== 'type' && at.name !== 'src') origSetAttribute.call(s, at.name, at.value);
      }
      if (el.onload) s.onload = el.onload;
      if (el.onerror) s.onerror = el.onerror;
      s.async = true;
      s.src = src;
      el.parentNode.replaceChild(s, el);
    }
  }

  function reloadKeepingScroll() {
    try { sessionStorage.setItem(SCROLL_KEY, JSON.stringify({ p: location.pathname, y: w.scrollY })); } catch (e) {}
    setTimeout(function () { location.reload(); }, 150);
  }
  try {
    var saved = JSON.parse(sessionStorage.getItem(SCROLL_KEY) || 'null');
    sessionStorage.removeItem(SCROLL_KEY);
    if (saved && saved.p === location.pathname && saved.y > 0) {
      w.addEventListener('load', function () { w.scrollTo({ top: saved.y, behavior: 'instant' }); });
    }
  } catch (e) {}

  var snapshot = gatedDenied(denied);
  function check() {
    var next = readDenied(), now = gatedDenied(next);
    if (now.join(',') === snapshot.join(',')) return;
    var revoked = now.some(function (c) { return snapshot.indexOf(c) === -1; });
    snapshot = now;
    denied = next;
    if (revoked) { reloadKeepingScroll(); return; }
    activateAllowed();
  }
  setInterval(check, 400);
  d.addEventListener('visibilitychange', check);

  /* ---- Public API ---- */
  w.cgAllowed = function (cat) { return !isDenied(cat); };
  w.cgLoad = function (cat, src, attrs) {
    if (isDenied(cat)) { hits++; return null; }
    var s = d.createElement('script');
    s.async = true;
    if (attrs) for (var k in attrs) if (Object.prototype.hasOwnProperty.call(attrs, k)) s.setAttribute(k, attrs[k]);
    s.src = src;
    var first = d.getElementsByTagName('script')[0];
    (first && first.parentNode ? first.parentNode : d.head).insertBefore(s, first || null);
    return s;
  };
  w.__cg = { denied: function () { return denied.slice(); }, hits: function () { return hits; } };
})(window, document);
